import type { MyAppsExternalApp } from '../api/myApps';
import type { MyAppsEntryCategory, MyAppsSearchResult } from './myAppsSearch';

export const CUSTOM_APP_LAUNCH_PREFIX = '/apps/link/';

const KNOWN_CATEGORIES: readonly MyAppsEntryCategory[] = [
  'Utilities',
  'Productivity',
  'IT Tools',
  'Analytics',
  'Microsoft 365'
];

export function customAppLaunchPath(slug: string): string {
  return `${CUSTOM_APP_LAUNCH_PREFIX}${encodeURIComponent(slug)}`;
}

export function parseCustomAppLaunchSlug(pathname: string): string | null {
  if (!pathname.startsWith(CUSTOM_APP_LAUNCH_PREFIX)) return null;
  const slug = decodeURIComponent(pathname.slice(CUSTOM_APP_LAUNCH_PREFIX.length).split('/')[0] ?? '');
  return slug.trim() ? slug : null;
}

export function isHttpUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' || url.protocol === 'http:';
  } catch {
    return false;
  }
}

export function openExternalAppUrl(url: string): boolean {
  if (!isHttpUrl(url)) return false;
  window.open(url, '_blank', 'noopener,noreferrer');
  return true;
}

export function isExternalMyAppsEntry(entry: {
  kind?: string;
  url?: string;
}): boolean {
  return entry.kind === 'link' || isHttpUrl(entry.url ?? '');
}

export function launchMyAppsEntry(
  entry: Pick<MyAppsSearchResult, 'id' | 'kind' | 'path' | 'url'>,
  navigate: (path: string) => void
): boolean {
  if (isExternalMyAppsEntry(entry)) {
    return openExternalAppUrl(entry.url ?? '');
  }
  navigate(entry.path);
  return true;
}

export function toExternalSearchResult(app: MyAppsExternalApp): MyAppsSearchResult {
  const category = KNOWN_CATEGORIES.includes(app.category as MyAppsEntryCategory)
    ? (app.category as MyAppsEntryCategory)
    : 'Utilities';
  return {
    id: app.id,
    title: app.title,
    description: app.description,
    path: app.path || customAppLaunchPath(app.id),
    icon: app.icon,
    searchText: [app.title, app.description, app.category, app.id].join(' ').toLocaleLowerCase(),
    kind: 'link',
    category,
    url: app.url
  };
}
